import { observable, action, runInAction, flow } from 'mobx';
import AuthService from '../../Auth/AuthService';
import { User } from 'oidc-client';
import { apiInstance } from '../../Api';
import { CurrentUserModel, TipoUsuario } from './Model';

const devAuthEnabled = process.env.REACT_APP_DEV_AUTH === 'true';

export class AuthStore {
    @observable public currentUser: CurrentUserModel = new CurrentUserModel();
    private user: User | null = null;
    private devProfile: Record<string, string> | null = null;
    private authService: AuthService = new AuthService();

    isLoggedIn = () => devAuthEnabled
      ? this.devProfile !== null
      : this.user !== null && this.user !== undefined;

    isTokenExpired = () => devAuthEnabled
      ? false
      : this.isLoggedIn() && (this.user && this.user.expired);

    renewToken = flow(function* (this: AuthStore) {
      if (devAuthEnabled)
        return;

      this.user = yield this.authService.renewToken();

      if (this.user) {
        this.currentUser.name = this.user.profile['Nome'];
        this.currentUser.tipoUsuario = this.retornaTipoUsuario();
        this.currentUser.cpf = this.retornaCpfUsuario();
      }
    });

    logout = () => {
      this.user = null;
      this.devProfile = null;
      this.currentUser = new CurrentUserModel();

      if (devAuthEnabled)
        return Promise.resolve();

      return this.authService.logout();
    }

    @action
    async login() {
      if (devAuthEnabled) {
        const result = await apiInstance.get<{ claims: Record<string, string> }>('dev/auth');
        runInAction(() => {
          this.devProfile = result.data.claims || {};
          this.currentUser.name = this.profileValue('Nome') || '';
          this.currentUser.tipoUsuario = this.retornaTipoUsuario();
          this.currentUser.cpf = this.retornaCpfUsuario();
        });
        return;
      }

      this.user = await this.authService.getUser();

      if (!this.isLoggedIn()) {
        await this.authService.login();
        this.user = await this.authService.getUser();
      }

      runInAction(() => {
        if (this.user) {
          this.currentUser.name = this.user.profile['Nome'];
          this.currentUser.tipoUsuario = this.retornaTipoUsuario();
          this.currentUser.cpf = this.retornaCpfUsuario();
        }
      });
    }

    signinRedirectCallback = async () => {
      this.user = await this.authService.signinRedirectCallback();
    }

    private profileValue(key: string) {
      if (this.devProfile && this.devProfile[key] !== undefined)
        return this.devProfile[key];

      if (this.user)
        return this.user.profile[key];

      return undefined;
    }

    private retornaTipoUsuario() {
      if (!this.devProfile && !this.user)
        return undefined;

      if (this.profileValue('Tcc.Funcionario') === '1')
        return TipoUsuario.Funcionario;

      switch (this.profileValue('Vinculo')) {
        case '0':
          return TipoUsuario.Aluno;
        case '4':
          return TipoUsuario.Professor;
        default:
          return undefined;
      }
    }

    private retornaCpfUsuario() {
      return this.profileValue('CPF') || '';
    }
}
